import { useEffect, useState } from "react";
import axios from "axios";
import { Reveal, SectionHeading } from "./Reveal";
import { ExternalLink, RefreshCw, Atom } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const NewsSection = () => {
  const { t, lang } = useLang();
  const [items, setItems] = useState([]);
  const [lastFetch, setLastFetch] = useState(null);
  const [quantumOnly, setQuantumOnly] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/news`)
      .then((r) => {
        setItems(r.data.items || []);
        setLastFetch(r.data.last_fetch);
      })
      .catch(() => {});
  }, []);

  const timeAgo = (iso) => {
    if (!iso) return "";
    const mins = Math.max(1, Math.floor((Date.now() - new Date(iso).getTime()) / 60000));
    if (mins < 60) return `${mins}${t("news.mAgo")}`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}${t("news.hAgo")}`;
    return `${Math.floor(hrs / 24)}${t("news.dAgo")}`;
  };

  const langItems = items.filter((i) => (i.lang || "en") === lang);
  const base = langItems.length ? langItems : items;
  const filtered = quantumOnly ? base.filter((i) => i.is_quantum) : base;

  return (
    <section id="news" data-testid="news-section" className="py-20 lg:py-28 bg-white relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeading
          eyebrow={t("news.eyebrow")}
          title={
            <>
              {t("news.titleA")}
              <span className="text-blue-700">{t("news.titleB")}</span>
            </>
          }
          sub={t("news.sub")}
        />

        <Reveal>
          <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
            {lastFetch ? (
              <p className="flex items-center gap-2 text-xs font-mono font-semibold text-slate-400">
                <RefreshCw className="w-3.5 h-3.5" />
                {t("news.updatedA")} {timeAgo(lastFetch)} · {t("news.refreshNote")}
              </p>
            ) : <span />}
            <div data-testid="news-filter" className="inline-flex p-1 rounded-full bg-slate-100 border border-slate-200">
              <button
                data-testid="news-filter-all"
                onClick={() => setQuantumOnly(false)}
                className={`px-4 py-2 rounded-full text-xs font-bold transition-colors duration-200 ${
                  !quantumOnly ? "bg-slate-900 text-white" : "text-slate-500 hover:text-slate-800"
                }`}
              >
                {t("news.filterAll")}
              </button>
              <button
                data-testid="news-filter-quantum"
                onClick={() => setQuantumOnly(true)}
                className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold transition-colors duration-200 ${
                  quantumOnly ? "bg-amber-500 text-white" : "text-slate-500 hover:text-slate-800"
                }`}
              >
                <Atom className="w-3.5 h-3.5" />
                {t("news.filterQuantum")}
              </button>
            </div>
          </div>
        </Reveal>

        {filtered.length === 0 ? (
          <p data-testid="news-empty" className="text-sm text-slate-500">{t("news.empty")}</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.slice(0, 6).map((n, i) => (
              <Reveal key={n.id} delay={i * 0.08}>
                <a
                  data-testid={`news-card-${i + 1}`}
                  href={n.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex flex-col h-full p-6 rounded-2xl border border-slate-200/90 bg-slate-50/50 hover:bg-white hover:border-blue-300 hover:shadow-[0_18px_44px_-18px_rgba(29,78,216,0.25)] hover:-translate-y-1 transition-all duration-400"
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-[10px] font-mono font-bold tracking-[0.14em] uppercase text-blue-700 bg-blue-50 border border-blue-200 px-2.5 py-1 rounded-full truncate max-w-[150px]">
                        {n.source || "News"}
                      </span>
                      {n.is_quantum && (
                        <span className="shrink-0 text-[9px] font-mono font-bold tracking-[0.14em] text-amber-700 bg-amber-50 border border-amber-300 px-2 py-1 rounded-full">
                          QUANTUM
                        </span>
                      )}
                    </div>
                    <span className="text-[11px] font-mono text-slate-400 whitespace-nowrap">{timeAgo(n.published)}</span>
                  </div>
                  <h3 className="mt-4 text-base font-bold text-slate-900 leading-snug group-hover:text-blue-700 transition-colors">
                    {n.title}
                  </h3>
                  {n.summary && (
                    <p className="mt-2.5 text-sm text-slate-500 leading-relaxed line-clamp-3">{n.summary}</p>
                  )}
                  <span className="mt-auto pt-4 inline-flex items-center gap-1.5 text-xs font-bold text-blue-700">
                    {t("news.readMore")}
                    <ExternalLink className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </span>
                </a>
              </Reveal>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};
